import type React from "react";
import { useCallback, useRef } from "react";
import useHoverable from "@Front/Hooks/useHoverable";
import useOpenable from "@Front/Hooks/useOpenable";
import { useCloseOpenableOnEscape } from "@Front/Hooks/useCloseOpenableOnEscape";
import { useCloseOpenableOnPageShow } from "@Front/Hooks/useCloseOpenableOnPageShow";
import { useCloseOpenableOnRouteChange } from "@Front/Hooks/useCloseOpenableOnRouteChange";
import type { IItem } from "./MenuItem";
import { useMenuEffects } from "./menuEffects";

type OpenSide = "left" | "right" | "center";

type SubmenuStateParams = {
	effectiveItems: IItem[];
	openingSide: OpenSide;
	openOnHover: boolean | undefined;
	isSuperAdmin: boolean;
	loadingPermissions: boolean;
};

export type MenuSubmenuInteraction = {
	toggle: () => void;
	handleMouseEnter: () => void;
	handleMouseLeave: () => void;
	iconRef: React.RefObject<HTMLDivElement | null>;
	subMenuRef: React.RefObject<HTMLDivElement | null>;
	shouldShowSubMenu: boolean;
	close: () => void;
};

export function useMenuSubmenuState(params: SubmenuStateParams): MenuSubmenuInteraction {
	const { effectiveItems, openingSide, openOnHover, isSuperAdmin, loadingPermissions } = params;
	const { handleMouseLeave, handleMouseEnter, isHovered, clearHover } = useHoverable(100);
	const { isOpen, toggle, close } = useOpenable();
	const subMenuRef = useRef<HTMLDivElement>(null);
	const iconRef = useRef<HTMLDivElement>(null);

	const dismiss = useCallback((): void => {
		close();
		clearHover();
	}, [clearHover, close]);

	const shouldShowSubMenu = (!openOnHover && isOpen) || (!!openOnHover && isHovered);

	useCloseOpenableOnRouteChange(shouldShowSubMenu, dismiss);
	useCloseOpenableOnPageShow(shouldShowSubMenu, dismiss);
	useCloseOpenableOnEscape(shouldShowSubMenu, dismiss);

	useMenuEffects({
		items: effectiveItems,
		openingSide,
		openOnHover,
		isSuperAdmin,
		loadingPermissions,
		subMenuRef: subMenuRef as React.RefObject<HTMLDivElement>,
		iconRef: iconRef as React.RefObject<HTMLDivElement>,
		close: dismiss,
		openOnHoverProp: openOnHover,
		isOpen,
		isHovered,
	});

	return { toggle, handleMouseEnter, handleMouseLeave, iconRef, subMenuRef, shouldShowSubMenu, close: dismiss };
}
